import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, FileText } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { GermanDateInput } from "@/components/GermanDateInput";
import HummingbirdBadgeCard from "@/components/HummingbirdBadgeCard";

export const openCertificate = async (certId) => {
  const win = window.open("", "_blank");
  try {
    const r = await api.get(`/audit/certificates/${certId}/file`, { responseType: "blob" });
    win.location.href = URL.createObjectURL(r.data);
  } catch (e) { win?.close(); toast.error("Datei konnte nicht geöffnet werden"); }
};

export default function MyCertificatesPage() {
  const [certs, setCerts] = useState([]);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = () => api.get("/audit/certificates/me").then((r) => setCerts(r.data));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!title.trim() || !date || !file) return toast.error("Bitte Titel, Datum und Datei angeben");
    const fd = new FormData();
    fd.append("title", title); fd.append("date", date); fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/audit/certificates", fd, { timeout: 60000 });
      toast.success("Zertifikat hochgeladen");
      setTitle(""); setDate(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`„${c.title}" wirklich löschen?`)) return;
    await api.delete(`/audit/certificates/${c.cert_id}`);
    load();
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-8" data-testid="my-certificates-page">
      <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">Qualifizierung</div>
      <h1 className="font-heading text-3xl font-light tracking-tighter text-slate-900">Meine Zertifikate</h1>
      <p className="text-sm text-slate-500 mt-1 mb-8">Laden Sie hier Nachweise Ihrer Fort- und Weiterbildungen hoch (PDF, PNG, JPG · max. 15 MB). Sie fließen in die Interne Audit ein.</p>
      <HummingbirdBadgeCard />
      <div className="border border-slate-200 bg-white p-5 grid grid-cols-1 md:grid-cols-[1fr_170px_1fr_auto] gap-3 items-end mb-8">
        <label className="text-xs text-slate-600">Titel der Schulung
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="input-tech mt-1" placeholder="z. B. Erste-Hilfe-Kurs" data-testid="cert-title-input" />
        </label>
        <label className="text-xs text-slate-600">Datum
          <GermanDateInput value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full" data-testid="cert-date-input" />
        </label>
        <div className="text-xs text-slate-600">Datei
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setFile(e.target.files[0] || null)} className="hidden" data-testid="cert-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="cert-file-pick-btn"
            className="mt-1 w-full input-tech text-left truncate">{file ? file.name : <span className="text-slate-400">Datei auswählen…</span>}</button>
        </div>
        <button onClick={upload} disabled={busy} data-testid="cert-upload-btn"
          className="px-4 py-2.5 text-xs font-medium bg-slate-900 text-white hover:bg-black disabled:opacity-50 flex items-center gap-1.5 justify-center">
          <Upload size={12} strokeWidth={1.5} /> {busy ? "Lädt hoch…" : "Hochladen"}
        </button>
      </div>
      <div className="border border-slate-200 bg-white divide-y divide-slate-100" data-testid="cert-list">
        {certs.length === 0 && <div className="p-8 text-center text-sm text-slate-500" data-testid="cert-empty">Noch keine Zertifikate hochgeladen.</div>}
        {certs.map((c) => (
          <div key={c.cert_id} className="p-4 flex items-center gap-3" data-testid={`cert-row-${c.cert_id}`}>
            <FileText size={16} strokeWidth={1.5} className="text-slate-400 shrink-0" />
            <button onClick={() => openCertificate(c.cert_id)} className="flex-1 text-left min-w-0" data-testid={`cert-open-${c.cert_id}`}>
              <div className="text-sm text-slate-900 truncate hover:underline">{c.title}</div>
              <div className="text-[11px] font-mono text-slate-400">{formatDeShort(c.date)} · {c.filename}</div>
            </button>
            <button onClick={() => remove(c)} className="text-slate-400 hover:text-red-600" data-testid={`cert-delete-${c.cert_id}`}><Trash2 size={14} strokeWidth={1.5} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
